<template>
  <div class="site-report-container w-full">
    <div class="site-report-header d-flex align-items-center justify-content-between flex-wrap">
      <div>
        <h2 class="report-title">Site Report & Analytics</h2>
        <p class="report-subtitle">
          Overview of website orders, online revenue, customer registrations, and category performance.
        </p>
      </div>

      <!-- Date Range Filter & Admin Export -->
      <div class="report-actions d-flex align-items-center flex-wrap gap-2">
        <div class="date-picker-group d-flex align-items-center">
          <div class="date-input-wrapper">
            <label class="date-label">From</label>
            <input
              type="date"
              v-model="startDate"
              class="form-control date-input"
              @change="onDateChange"
            />
          </div>
          <span class="date-separator">to</span>
          <div class="date-input-wrapper">
            <label class="date-label">To</label>
            <input
              type="date"
              v-model="endDate"
              class="form-control date-input"
              @change="onDateChange"
            />
          </div>
        </div>

        <div class="preset-buttons d-flex align-items-center">
          <button
            type="button"
            class="preset-btn"
            :class="{ active: activePreset === '3months' }"
            @click="set3MonthsPreset"
          >
            Last 3 Months
          </button>
          <button
            type="button"
            class="preset-btn"
            :class="{ active: activePreset === '30days' }"
            @click="set30DaysPreset"
          >
            Last 30 Days
          </button>
          <button
            type="button"
            class="preset-btn"
            :class="{ active: activePreset === 'ytd' }"
            @click="setYTDPreset"
          >
            Year to Date
          </button>
        </div>

        <!-- Export Button for Admin -->
        <md-button
          class="md-primary button-icon export-btn"
          @click="exportReport"
          :disabled="exporting || salesReportLoading"
        >
          <span v-if="!exporting">Export Report</span>
          <span v-else>Exporting...</span>
          <img src="@/assets/svg/excel.svg" alt="Export Excel" />
        </md-button>
      </div>
    </div>

    <!-- Key Metrics Cards Grid -->
    <div class="grid-report-cards mt-4">
      <!-- Card 1: Website Orders -->
      <div class="report-card">
        <div class="card-icon-wrapper icon-orders">
          <BasketIcon />
        </div>
        <div class="card-body-content">
          <span class="card-label">Website Orders</span>
          <div v-if="salesReportLoading" class="skeleton-loader skeleton-title mt-1"></div>
          <h3 v-else class="card-value">{{ reportMetrics.totalOrders | formatNumber }}</h3>
          
          <div class="card-meta">
            <template v-if="salesReportLoading">
              <span class="skeleton-loader skeleton-badge"></span>
            </template>
            <template v-else>
              <span class="badge badge-success">{{ reportMetrics.completedOrders | formatNumber }} Delivered</span>
              <span class="meta-subtext">Selected Range</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Card 2: Total Online Sales / Revenue -->
      <div class="report-card">
        <div class="card-icon-wrapper icon-revenue">
          <WalletIcon />
        </div>
        <div class="card-body-content">
          <span class="card-label">Total Online Sales / Revenue</span>
          <div v-if="salesReportLoading" class="skeleton-loader skeleton-title mt-1"></div>
          <h3 v-else class="card-value">{{ reportMetrics.totalRevenue | formatMoney }}</h3>
          
          <div class="card-meta">
            <template v-if="salesReportLoading">
              <span class="skeleton-loader skeleton-badge"></span>
            </template>
            <template v-else>
              <span class="meta-subtext">Avg ₦{{ reportMetrics.dailyAvgRevenue | formatNumber }}/day</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Card 3: Average Order Value (AOV) -->
      <div class="report-card">
        <div class="card-icon-wrapper icon-aov">
          <CartIcon />
        </div>
        <div class="card-body-content">
          <span class="card-label">Average Order Value (AOV)</span>
          <div v-if="salesReportLoading" class="skeleton-loader skeleton-title mt-1"></div>
          <h3 v-else class="card-value">{{ reportMetrics.averageOrderValue | formatMoney }}</h3>
          
          <div class="card-meta">
            <template v-if="salesReportLoading">
              <span class="skeleton-loader skeleton-badge"></span>
            </template>
            <template v-else>
              <span class="meta-subtext">Revenue ÷ Website Orders</span>
            </template>
          </div>
        </div>
      </div>

      <!-- Card 4: Number of Registered Customers -->
      <div class="report-card">
        <div class="card-icon-wrapper icon-users">
          <CartIcon2 />
        </div>
        <div class="card-body-content">
          <span class="card-label">Registered Customers</span>
          <div v-if="salesReportLoading" class="skeleton-loader skeleton-title mt-1"></div>
          <h3 v-else class="card-value">{{ reportMetrics.registeredCustomers | formatNumber }}</h3>
          
          <div class="card-meta">
            <template v-if="salesReportLoading">
              <span class="skeleton-loader skeleton-badge"></span>
            </template>
            <template v-else>
              <span class="badge badge-info">+{{ reportMetrics.newCustomersInPeriod }} New</span>
              <span class="meta-subtext">Total Buyers</span>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Monthly / Period Breakdown & Best Selling Categories Section -->
    <div class="row mt-4">
      <!-- Period Sales & Revenue Breakdown -->
      <div class="col-lg-7 col-md-12 mb-4">
        <div class="report-section-card">
          <div class="card-header-bar d-flex justify-content-between align-items-center">
            <h4>Sales & Revenue Breakdown by Period</h4>
            <span class="range-badge">{{ formatDate(startDate) }} – {{ formatDate(endDate) }}</span>
          </div>
          <div class="table-responsive mt-3">
            <table class="table table-hover report-table">
              <thead>
                <tr>
                  <th>Period / Month</th>
                  <th class="text-right">Website Orders</th>
                  <th class="text-right">Total Revenue (₦)</th>
                  <th class="text-right">Avg Order Value</th>
                  <th class="text-right">New Customers</th>
                </tr>
              </thead>
              
              <!-- Loading Skeleton Rows -->
              <tbody v-if="salesReportLoading">
                <tr v-for="n in 4" :key="'sk-period-' + n">
                  <td><div class="skeleton-loader skeleton-text" style="width: 90px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 50px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 90px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 80px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 40px"></div></td>
                </tr>
              </tbody>

              <!-- Empty State -->
              <tbody v-else-if="periodBreakdown.length === 0">
                <tr>
                  <td colspan="5" class="text-center py-4 text-muted">
                    No sales data recorded for the selected date range.
                  </td>
                </tr>
              </tbody>

              <!-- Actual Data Rows -->
              <tbody v-else>
                <tr v-for="(period, idx) in periodBreakdown" :key="idx">
                  <td class="font-weight-bold">{{ period.monthName }}</td>
                  <td class="text-right">{{ period.orders | formatNumber }}</td>
                  <td class="text-right text-success font-weight-bold">{{ period.revenue | formatMoney }}</td>
                  <td class="text-right">{{ period.aov | formatMoney }}</td>
                  <td class="text-right">{{ period.newCustomers | formatNumber }}</td>
                </tr>
              </tbody>

              <tfoot v-if="!salesReportLoading && periodBreakdown.length > 0">
                <tr class="font-weight-bold footer-row">
                  <td>TOTAL</td>
                  <td class="text-right">{{ reportMetrics.totalOrders | formatNumber }}</td>
                  <td class="text-right text-success">{{ reportMetrics.totalRevenue | formatMoney }}</td>
                  <td class="text-right">{{ reportMetrics.averageOrderValue | formatMoney }}</td>
                  <td class="text-right">{{ reportMetrics.newCustomersInPeriod | formatNumber }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <!-- Best Selling Categories Table -->
      <div class="col-lg-5 col-md-12 mb-4">
        <div class="report-section-card">
          <div class="card-header-bar d-flex justify-content-between align-items-center">
            <h4>Best Selling Categories</h4>
            <span class="range-badge">Top Categories</span>
          </div>
          <div class="table-responsive mt-3">
            <table class="table table-hover report-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th class="text-right">Items Sold</th>
                  <th class="text-right">Orders</th>
                  <th class="text-right">Revenue</th>
                  <th class="text-center">Share</th>
                </tr>
              </thead>

              <!-- Loading Skeleton Rows -->
              <tbody v-if="salesReportLoading">
                <tr v-for="n in 4" :key="'sk-cat-' + n">
                  <td><div class="skeleton-loader skeleton-text" style="width: 100px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 40px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 40px"></div></td>
                  <td class="text-right"><div class="skeleton-loader skeleton-text" style="width: 70px"></div></td>
                  <td class="text-center"><div class="skeleton-loader skeleton-text" style="width: 50px"></div></td>
                </tr>
              </tbody>

              <!-- Empty State -->
              <tbody v-else-if="bestSellingCategories.length === 0">
                <tr>
                  <td colspan="5" class="text-center py-4 text-muted">
                    No category sales recorded for the selected date range.
                  </td>
                </tr>
              </tbody>

              <!-- Actual Data Rows -->
              <tbody v-else>
                <tr v-for="(cat, idx) in bestSellingCategories" :key="idx">
                  <td>
                    <div class="d-flex align-items-center">
                      <span class="cat-rank">{{ idx + 1 }}</span>
                      <span class="cat-name">{{ cat.name }}</span>
                    </div>
                  </td>
                  <td class="text-right">{{ cat.itemsSold | formatNumber }}</td>
                  <td class="text-right">{{ cat.ordersCount | formatNumber }}</td>
                  <td class="text-right font-weight-bold">{{ cat.revenue | formatMoney }}</td>
                  <td class="text-center">
                    <div class="share-progress">
                      <div class="progress-bar-bg">
                        <div class="progress-bar-fill" :style="{ width: (cat.share || 0) + '%' }"></div>
                      </div>
                      <small>{{ cat.share || 0 }}%</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import fileDownload from "js-file-download";
import moment from "moment";
import {
  WalletIcon,
  BasketIcon,
  CartIcon,
  CartIcon2
} from "@/components";

export default {
  name: "SiteReportSection",
  components: {
    WalletIcon,
    BasketIcon,
    CartIcon,
    CartIcon2
  },
  data() {
    const end = moment().format("YYYY-MM-DD");
    const start = moment().subtract(3, "months").startOf("month").format("YYYY-MM-DD");

    return {
      startDate: start,
      endDate: end,
      activePreset: "3months",
      exporting: false
    };
  },
  computed: {
    ...mapGetters("dataStore", [
      "salesReportData",
      "salesReportLoading"
    ]),

    reportMetrics() {
      const apiData = this.salesReportData || {};
      const days = moment(this.endDate).diff(moment(this.startDate), "days") || 1;

      const totalOrders = apiData.total_orders !== undefined ? parseInt(apiData.total_orders || 0) : 0;
      const totalRevenue = apiData.total_revenue !== undefined ? parseFloat(apiData.total_revenue || 0) : 0;
      const completedOrders = apiData.completed_orders !== undefined ? parseInt(apiData.completed_orders || 0) : 0;
      const registeredCustomers = apiData.total_customers !== undefined ? parseInt(apiData.total_customers || 0) : 0;
      const newCustomersInPeriod = apiData.new_customers !== undefined ? parseInt(apiData.new_customers || 0) : 0;
      const averageOrderValue = apiData.average_order_value !== undefined ? parseFloat(apiData.average_order_value || 0) : (totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0);
      const dailyAvgRevenue = Math.round(totalRevenue / (days || 1));

      return {
        totalOrders,
        completedOrders,
        totalRevenue,
        averageOrderValue,
        registeredCustomers,
        newCustomersInPeriod,
        dailyAvgRevenue
      };
    },

    periodBreakdown() {
      if (this.salesReportData && Array.isArray(this.salesReportData.period_breakdown)) {
        return this.salesReportData.period_breakdown;
      }
      return [];
    },

    bestSellingCategories() {
      if (this.salesReportData && Array.isArray(this.salesReportData.best_selling_categories)) {
        return this.salesReportData.best_selling_categories;
      }
      return [];
    }
  },

  methods: {
    ...mapActions("dataStore", [
      "getSalesReport"
    ]),

    formatDate(dateStr) {
      return moment(dateStr).format("MMM D, YYYY");
    },

    fetchReportData() {
      this.getSalesReport({
        start_date: this.startDate,
        end_date: this.endDate
      });
    },

    onDateChange() {
      this.activePreset = "custom";
      this.fetchReportData();
    },

    set3MonthsPreset() {
      this.activePreset = "3months";
      this.endDate = moment().format("YYYY-MM-DD");
      this.startDate = moment().subtract(3, "months").startOf("month").format("YYYY-MM-DD");
      this.fetchReportData();
    },

    set30DaysPreset() {
      this.activePreset = "30days";
      this.endDate = moment().format("YYYY-MM-DD");
      this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
      this.fetchReportData();
    },

    setYTDPreset() {
      this.activePreset = "ytd";
      this.endDate = moment().format("YYYY-MM-DD");
      this.startDate = moment().startOf("year").format("YYYY-MM-DD");
      this.fetchReportData();
    },

    // Admin Export Feature based on start and end date
    async exportReport() {
      try {
        this.exporting = true;

        // Try triggering API export first if backend endpoint exists
        await this.$store.dispatch("auth/export", {
          model: "site-report",
          start_date: this.startDate,
          end_date: this.endDate
        }).catch(() => null);

        // Generate client CSV export matching exact selected date range
        let csvContent = "";
        csvContent += "BAML SITE REPORT & ANALYTICS\n";
        csvContent += `Selected Date Range:,${this.startDate} to ${this.endDate}\n`;
        csvContent += `Report Generated On:,${moment().format("YYYY-MM-DD HH:mm:ss")}\n\n`;

        csvContent += "EXECUTIVE SUMMARY\n";
        csvContent += "Metric,Value\n";
        csvContent += `Website Orders,${this.reportMetrics.totalOrders}\n`;
        csvContent += `Delivered Orders,${this.reportMetrics.completedOrders}\n`;
        csvContent += `Total Revenue (NGN),${this.reportMetrics.totalRevenue}\n`;
        csvContent += `Average Order Value (AOV) (NGN),${this.reportMetrics.averageOrderValue}\n`;
        csvContent += `Registered Customers,${this.reportMetrics.registeredCustomers}\n`;
        csvContent += `New Customers in Period,${this.reportMetrics.newCustomersInPeriod}\n\n`;

        csvContent += "SALES & REVENUE BREAKDOWN BY PERIOD\n";
        csvContent += "Period / Month,Website Orders,Total Revenue (NGN),Average Order Value (NGN),New Customers\n";
        this.periodBreakdown.forEach((p) => {
          csvContent += `"${p.monthName}",${p.orders},${p.revenue},${p.aov},${p.newCustomers}\n`;
        });
        csvContent += "\n";

        csvContent += "BEST SELLING CATEGORIES\n";
        csvContent += "Category Name,Items Sold,Orders Count,Total Revenue (NGN),Market Share (%)\n";
        this.bestSellingCategories.forEach((cat) => {
          csvContent += `"${cat.name}",${cat.itemsSold},${cat.ordersCount},${cat.revenue},${cat.share || 0}%\n`;
        });

        const filename = `Site_Report_${this.startDate}_to_${this.endDate}.csv`;
        fileDownload(csvContent, filename);

        if (this.$swal) {
          this.$swal({
            icon: "success",
            title: "Report Exported",
            text: `Site report for ${this.startDate} to ${this.endDate} has been exported successfully.`
          });
        }
      } catch (err) {
        console.error("Export error:", err);
      } finally {
        this.exporting = false;
      }
    }
  },

  mounted() {
    this.fetchReportData();
  }
};
</script>

<style lang="scss" scoped>
.site-report-container {
  background: #f9fbfd;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
}

.report-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 4px 0;
}

.report-subtitle {
  font-size: 13px;
  color: #718096;
  margin: 0;
}

.report-actions {
  gap: 12px;
}

.date-picker-group {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;

  .date-input-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;

    .date-label {
      font-size: 11px;
      font-weight: 600;
      color: #a0aec0;
      margin: 0;
      text-transform: uppercase;
    }

    .date-input {
      border: none;
      padding: 4px 8px;
      font-size: 13px;
      font-weight: 500;
      color: #2d3748;
      background: transparent;
      outline: none;

      &:focus {
        box-shadow: none;
      }
    }
  }

  .date-separator {
    margin: 0 8px;
    font-size: 12px;
    color: #cbd5e0;
    font-weight: 600;
  }
}

.preset-buttons {
  background: #edf2f7;
  border-radius: 8px;
  padding: 3px;

  .preset-btn {
    border: none;
    background: transparent;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      color: #1a202c;
    }

    &.active {
      background: #ffffff;
      color: #e53e3e;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
}

.export-btn {
  height: 42px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 13px;

  img {
    width: 18px;
    height: 18px;
  }
}

/* Skeleton Loading Animation */
.skeleton-loader {
  background: linear-gradient(90deg, #edf2f7 25%, #e2e8f0 50%, #edf2f7 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-text {
  height: 16px;
  display: inline-block;
}

.skeleton-title {
  height: 28px;
  width: 110px;
  margin-bottom: 8px;
}

.skeleton-badge {
  height: 14px;
  width: 70px;
  display: inline-block;
}

/* Cards Grid */
.grid-report-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 20px;
}

.report-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  .card-icon-wrapper {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;

    &.icon-orders {
      background: #ebf8ff;
      color: #3182ce;
    }
    &.icon-revenue {
      background: #f0fff4;
      color: #38a169;
    }
    &.icon-aov {
      background: #faf5ff;
      color: #805ad5;
    }
    &.icon-users {
      background: #fff5f5;
      color: #e53e3e;
    }
  }

  .card-label {
    font-size: 12px;
    font-weight: 600;
    color: #718096;
    display: block;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .card-value {
    font-size: 22px;
    font-weight: 700;
    color: #1a202c;
    margin: 0 0 10px 0;
  }

  .card-meta {
    display: flex;
    align-items: center;
    gap: 8px;

    .meta-subtext {
      font-size: 11px;
      color: #a0aec0;
    }
  }
}

.report-section-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

  .card-header-bar {
    border-bottom: 1px solid #edf2f7;
    padding-bottom: 12px;

    h4 {
      font-size: 16px;
      font-weight: 700;
      color: #2d3748;
      margin: 0;
    }

    .range-badge {
      font-size: 11px;
      font-weight: 600;
      background: #edf2f7;
      color: #4a5568;
      padding: 4px 10px;
      border-radius: 20px;
    }
  }
}

.report-table {
  width: 100%;

  th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #718096;
    border-top: none;
    border-bottom: 2px solid #edf2f7;
    padding: 10px;
  }

  td {
    font-size: 13px;
    color: #2d3748;
    vertical-align: middle;
    padding: 12px 10px;
    border-bottom: 1px solid #f7fafc;
  }

  .footer-row {
    background: #f7fafc;
    td {
      font-size: 13px;
      border-top: 2px solid #e2e8f0;
    }
  }
}

.cat-rank {
  width: 22px;
  height: 22px;
  background: #edf2f7;
  color: #4a5568;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.share-progress {
  display: flex;
  flex-direction: column;
  align-items: center;

  .progress-bar-bg {
    width: 60px;
    height: 6px;
    background: #edf2f7;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 2px;

    .progress-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #ed1c24, #ff6b6b);
      border-radius: 3px;
    }
  }

  small {
    font-size: 10px;
    color: #718096;
    font-weight: 600;
  }
}
</style>
